// Lens display tables + helpers consumed by FlaskPanel (grouping) and
// FlaskRow (chem-tag short labels) and RowSupplyIcons (GHS tooltips).
//
// Spec: §8.4 / §8.5 / Appendix of 2026-05-11-current-flask-server-data-design.

import type { SubstanceLensInfo } from "@/utils/types/substance";

// Canonical FDA technical-effect codes (the 43 surfaced by the appendix).
// Order is the canonical sort: flavor-relevant first, then sweeteners,
// nutrients, structural, regulatory roles.
export const FDA_TECHNICAL_EFFECTS: string[] = [
  "FLAVORING AGENT OR ADJUVANT",
  "FLAVOR ENHANCER",
  "NUTRITIVE SWEETENER",
  "NON-NUTRITIVE SWEETENER",
  "NUTRIENT SUPPLEMENT",
  "STABILIZER OR THICKENER",
  "PROCESSING AID",
  "EMULSIFIER OR EMULSIFIER SALT",
  "COLOR OR COLORING ADJUNCT",
  "FORMULATION AID",
  "ANTICAKING AGENT OR FREE-FLOW AGENT",
  "TEXTURIZER",
  "SOLVENT OR VEHICLE",
  "PH CONTROL AGENT",
  "ENZYME",
  "SURFACE-ACTIVE AGENT",
  "ANTIMICROBIAL AGENT",
  "SURFACE-FINISHING AGENT",
  "LUBRICANT OR RELEASE AGENT",
  "DRYING AGENT",
  "ANTIOXIDANT",
  "MASTICATORY SUBSTANCE",
  "HUMECTANT",
  "LEAVENING AGENT",
  "DOUGH STRENGTHENER",
  "SEQUESTRANT",
  "MALTING OR FERMENTING AID",
  "FIRMING AGENT",
  "FLOUR TREATING AGENT",
  "BOILER WATER ADDITIVE",
  "PROPELLANT",
  "OXIDIZING OR REDUCING AGENT",
  "WASHING OR SURFACE REMOVAL AGENT",
  "FUMIGANT",
  "SYNERGIST",
  "CURING OR PICKLING AGENT",
  "DIRECT CONTACT",
  "FREEZING OR COOLING AGENT",
  "TRACER",
  "PRESERVATIVE",
  "PROCESSING",
  "SOLVENT",
];

export function toTitleCase(s: string): string {
  return s.toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

export const ROLE_DISPLAY: Record<string, string> = Object.fromEntries(
  FDA_TECHNICAL_EFFECTS.map((code) => [code, toTitleCase(code)]),
);
export const ROLE_SORT_ORDER: Record<string, number> = Object.fromEntries(
  FDA_TECHNICAL_EFFECTS.map((code, i) => [code, i]),
);

export const SOLVENT_DISPLAY: Record<string, string> = {
  water:   "Water-soluble",
  oil:     "Oil-soluble",
  mixture: "Mixture",
  unknown: "Unknown",
};
export const SOLVENT_SORT_ORDER: Record<string, number> = {
  water:   0,
  oil:     1,
  mixture: 2,
  unknown: 3,
};

export const GHS_PICTOGRAM_NAMES: Record<string, string> = {
  GHS01: "Explosive",
  GHS02: "Flammable",
  GHS03: "Oxidizing",
  GHS04: "Compressed gas",
  GHS05: "Corrosive",
  GHS06: "Acute toxicity",
  GHS07: "Irritant / harmful",
  GHS08: "Health hazard",
  GHS09: "Environmental",
};

// chemShort('Esters') → 'EST'. Used for the FlaskRow chem-tag chip.
// Returns the placeholder '—' (em-dash) for null/empty.
export function chemShort(chemClass: string | null | undefined): string {
  if (!chemClass) return "—";
  const stripped = chemClass.replace(/[^A-Za-z]/g, "");
  return stripped.slice(0, 3).toUpperCase() || "—";
}

type LensTab = "role" | "solvent" | "chem";

export function bucketForLens(
  lens: SubstanceLensInfo,
  tab: LensTab,
): { key: string; label: string; sortIdx: number } {
  if (tab === "role") {
    const key = lens.primaryRole ?? "UNKNOWN";
    return {
      key,
      label:   ROLE_DISPLAY[key] ?? toTitleCase(key),
      sortIdx: ROLE_SORT_ORDER[key] ?? 99,
    };
  }
  if (tab === "solvent") {
    const key = (lens.solubility ?? "unknown") as keyof typeof SOLVENT_DISPLAY;
    return {
      key,
      label:   SOLVENT_DISPLAY[key] ?? key,
      sortIdx: SOLVENT_SORT_ORDER[key] ?? 99,
    };
  }
  const key = lens.chemClass ?? "Other";
  return { key, label: key, sortIdx: 0 };
}
